import React from 'react';
import { cn } from '@/lib/utils';

const TONES = {
  success: {
    pill: 'bg-emerald-500/15 text-emerald-800 ring-emerald-500/25 dark:text-emerald-200',
    text: 'text-emerald-700 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  info: {
    pill: 'bg-sky-500/15 text-sky-800 ring-sky-500/25 dark:text-sky-200',
    text: 'text-sky-700 dark:text-sky-300',
    dot: 'bg-sky-500',
  },
  warning: {
    pill: 'bg-amber-500/15 text-amber-900 ring-amber-500/30 dark:text-amber-100',
    text: 'text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  danger: {
    pill: 'bg-red-500/15 text-red-800 ring-red-500/25 dark:text-red-200',
    text: 'text-red-700 dark:text-red-300',
    dot: 'bg-red-500',
  },
  violet: {
    pill: 'bg-violet-500/15 text-violet-800 ring-violet-500/25 dark:text-violet-200',
    text: 'text-violet-700 dark:text-violet-300',
    dot: 'bg-violet-500',
  },
  neutral: {
    pill: 'bg-foreground/5 text-muted-foreground ring-foreground/10',
    text: 'text-muted-foreground',
    dot: 'bg-muted-foreground/70',
  },
};

const STATUS_TONE = {
  Available: 'success',
  online: 'success',
  Preparing: 'info',
  Charging: 'info',
  Reserved: 'info',
  Minor: 'info',
  SuspendedEV: 'warning',
  SuspendedEVSE: 'warning',
  Acknowledged: 'warning',
  Major: 'warning',
  Finishing: 'violet',
  Faulted: 'danger',
  Active: 'danger',
  Critical: 'danger',
  Finished: 'neutral',
  Cleared: 'neutral',
  Unavailable: 'neutral',
  offline: 'neutral',
};

const toneFor = (status) => TONES[STATUS_TONE[status]] || TONES.neutral;

const sizeClasses = {
  xs: 'px-2 py-0.5 text-[11px]',
  sm: 'px-2.5 py-0.5 text-xs',
  md: 'px-3 py-1 text-sm',
};

export const StatusBadge = ({ status, variant = 'default', size = 'sm', className }) => {
  const tone = toneFor(status);

  if (variant === 'dot') {
    return (
      <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap', className)}>
        <span className={cn('h-2 w-2 rounded-full', tone.dot, status === 'Charging' && 'animate-pulse')} />
        <span className={cn('text-sm font-medium', tone.text)}>{status}</span>
      </span>
    );
  }

  if (variant === 'outline') {
    return (
      <span className={cn('inline-flex items-center whitespace-nowrap rounded-full font-medium ring-1 ring-inset ring-current', sizeClasses[size], tone.text, className)}>
        {status}
      </span>
    );
  }

  return (
    <span className={cn('inline-flex items-center whitespace-nowrap rounded-full font-medium ring-1 ring-inset', sizeClasses[size], tone.pill, className)}>
      {status}
    </span>
  );
};

export const SeverityBadge = ({ severity, className }) => {
  const tone = toneFor(severity);
  return (
    <span className={cn('inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset', tone.pill, className)}>
      {severity}
    </span>
  );
};

export const OnlineIndicator = ({ isOnline, showLabel = true, className }) => {
  const tone = isOnline ? TONES.success : TONES.neutral;
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap', className)}>
      <span className={cn('h-2 w-2 rounded-full', tone.dot, isOnline && 'animate-pulse-status')} />
      {showLabel && (
        <span className={cn('text-sm font-medium', tone.text)}>
          {isOnline ? 'Online' : 'Offline'}
        </span>
      )}
    </span>
  );
};

export default StatusBadge;
